<script>
    import { onMount } from "svelte";
    import { afterNavigate, beforeNavigate, goto } from "$app/navigation";
    import { page } from "$app/stores";
    import { initKonamiListener } from "$lib/utils/konamiCode";
    import { startWhirlpoolTransition } from "$lib/utils/whirlpoolTransition";
    import SvgDefs from "$lib/components/SvgDefs.svelte";
    import GlobalNav from "$lib/components/GlobalNav.svelte";
    import SiteFooter from "$lib/components/SiteFooter.svelte";
    import RainLayer from "$lib/components/RainLayer.svelte";
    import StarfieldBg from "$lib/components/StarfieldBg.svelte";
    import CloudLayer from "$lib/components/CloudLayer.svelte";
    import { loadAmbientScripts, loadCoreScripts } from "$lib/utils/scripts";
    import { initSmoothScroll } from "$lib/utils/smoothScroll";
    import { registerServiceWorker } from "$lib/utils/serviceWorker";
    import { registerWebMCP } from "$lib/utils/webmcp";
    import { theme } from "$lib/utils/theme.svelte.ts";

    // Standalone pages render without the site chrome, global CSS or ambient effects.
    const STANDALONE = new Set(["/warrior-of-the-gods", "/bag", "/stats"]);
    const isStandalone = (pathname) => STANDALONE.has(pathname);
    // Pages that also skip the global stylesheet and fonts (they bring their own).
    const BARE = new Set(["/bag", "/stats"]);
    const isBare = (pathname) => BARE.has(pathname);

    beforeNavigate((navigation) => {
        if (typeof window === "undefined") {
            return;
        }

        if (navigation.willUnload) {
            return;
        }

        const toUrl = navigation.to?.url;
        if (!toUrl || toUrl.origin !== window.location.origin) {
            return;
        }

        const isSameDocument =
            toUrl.pathname === window.location.pathname &&
            toUrl.search === window.location.search;
        if (isSameDocument && toUrl.hash) {
            return;
        }

        const lightningApi = window.__dingLightningTransition;
        if (lightningApi?.playEffect) {
            lightningApi.playEffect();
        }
    });

    let konami = null;

    afterNavigate(() => {
        if (typeof window !== "undefined") {
            if (!isStandalone(window.location.pathname)) {
                window.__dingReinitPage?.();
                window.__dingInitRipple?.();
                loadAmbientScripts().catch((error) => {
                    console.error("Failed to load ambient scripts:", error);
                });
            }
            konami?.reset();
        }
    });

    onMount(() => {
        theme.init();
        let destroySmoothScroll = () => {};
        let currentBodyClass = "";
        let smoothScrollScheduled = null;
        let smoothScrollInitToken = 0;
        let smoothScrollActive = false;

        const cancelScheduledSmoothScroll = () => {
            if (!smoothScrollScheduled || typeof window === "undefined") return;
            if (
                smoothScrollScheduled.type === "idle" &&
                "cancelIdleCallback" in window
            ) {
                window.cancelIdleCallback(smoothScrollScheduled.id);
            } else {
                clearTimeout(smoothScrollScheduled.id);
            }
            smoothScrollScheduled = null;
        };

        const stopSmoothScroll = () => {
            cancelScheduledSmoothScroll();
            if (!smoothScrollActive) return;
            destroySmoothScroll();
            destroySmoothScroll = () => {};
            smoothScrollActive = false;
        };

        const scheduleSmoothScroll = () => {
            cancelScheduledSmoothScroll();
            const token = ++smoothScrollInitToken;
            const run = async () => {
                smoothScrollScheduled = null;
                const destroy = await initSmoothScroll();
                if (token !== smoothScrollInitToken) {
                    destroy();
                    return;
                }
                destroySmoothScroll = destroy;
                smoothScrollActive = true;
            };
            if ("requestIdleCallback" in window) {
                smoothScrollScheduled = {
                    type: "idle",
                    id: requestIdleCallback(() => run(), { timeout: 2000 }),
                };
            } else {
                smoothScrollScheduled = {
                    type: "timeout",
                    id: setTimeout(() => run(), 200),
                };
            }
        };

        const syncSmoothScroll = (pathname) => {
            const shouldEnable = !isStandalone(pathname);
            if (!shouldEnable) {
                smoothScrollInitToken += 1;
                stopSmoothScroll();
                return;
            }
            if (smoothScrollActive || smoothScrollScheduled) return;
            scheduleSmoothScroll();
        };

        const unsubscribe = page.subscribe(($page) => {
            const nextClass = $page.data.bodyClass ?? "";
            if (currentBodyClass) {
                currentBodyClass.split(" ").forEach((cls) => {
                    if (cls) document.body.classList.remove(cls);
                });
            }
            if (nextClass) {
                nextClass.split(" ").forEach((cls) => {
                    if (cls) document.body.classList.add(cls);
                });
            }
            currentBodyClass = nextClass;
            syncSmoothScroll($page.url.pathname);
        });

        const initCoreScripts = async () => {
            registerServiceWorker();
            registerWebMCP();
            if (isBare(window.location.pathname)) return;

            try {
                await loadCoreScripts({
                    includeAmbient: !isStandalone(window.location.pathname),
                });
            } catch (error) {
                console.error("Failed to load core scripts:", error);
            }
        };

        initCoreScripts();

        konami = initKonamiListener(() => {
            if (isStandalone(window.location.pathname)) return;
            const audio = new Audio("/sounds/whirlpool.mp3");
            audio.volume = 0.7;
            audio.play().catch(() => {});
            startWhirlpoolTransition(() => goto("/warrior-of-the-gods"));
        });

        return () => {
            unsubscribe();
            smoothScrollInitToken += 1;
            stopSmoothScroll();
            konami.destroy();
        };
    });
</script>

<svelte:head>
    <!-- Critical CSS: Above The Fold -->
    <style>
        .global-nav .navbar-nav {
            width: 100%;
            display: flex;
            flex-wrap: no-wrap;
            justify-content: space-between;
        }
        .icon {
            display: inline-block;
            width: 1em;
            height: 1em;
            stroke-width: 0;
            stroke: currentColor;
            fill: currentColor;
        }
        .navbar-toggler .icon {
            font-size: 44px;
        }
        .down-arrow {
            font-size: 44px;
        }
        .down-arrow {
            animation: bounce 2s infinite ease;
            display: block;
            margin: 0 auto;
            font-size: 33px;
        }
        /* Theme + Sound toggles — critical shape to prevent FOUC */
        .nav-controls .toggle,
        .nav-controls .sound-toggle {
            --width: clamp(46px, 8vw, 72px);
            --ar: 8 / 3;
            width: var(--width);
            aspect-ratio: var(--ar);
            border-radius: 100vh;
            border: 0;
            padding: 0;
            overflow: hidden;
            cursor: pointer;
            position: relative;
            background: light-dark(hsl(204 53% 47%), hsl(229 25% 16%));
            isolation: isolate;
        }
    </style>

    <meta charset="UTF-8" />
    <meta name="robots" content="index, follow" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />

    <link rel="icon" type="image/png" href="/favicons/favicon-96x96.png" sizes="96x96" />
    <link rel="icon" type="image/svg+xml" href="/favicons/favicon.svg" />
    <link rel="shortcut icon" href="/favicons/favicon.ico" />
    <link rel="apple-touch-icon" sizes="180x180" href="/favicons/apple-touch-icon.png" />
    {#if !isBare($page.url.pathname)}
    <meta name="apple-mobile-web-app-title" content="dinglandia" />
    <link rel="manifest" href="/favicons/site.webmanifest" />
    <meta name="msapplication-TileColor" content="#00b0ff" />
    <meta
        name="theme-color"
        content={theme.resolved === "dark" ? "#1a1f2e" : "#00b0ff"}
    />

    <link rel="preconnect" href="https://cdn.jsdelivr.net" crossorigin />

    <link
        rel="preload"
        href="/fonts/Skranji-Bold.woff2"
        as="font"
        type="font/woff2"
        crossorigin
        fetchpriority="high"
    />
    <link
        rel="preload"
        href="/fonts/Lato-Regular.woff2"
        as="font"
        type="font/woff2"
        crossorigin
        fetchpriority="high"
    />
    <link
        rel="preload"
        href="/fonts/Lato-Italic.woff2"
        as="font"
        type="font/woff2"
        crossorigin
        fetchpriority="high"
    />

    {#if !isStandalone($page.url.pathname)}
        <link
            rel="preload"
            href="/img/intro-video.avif"
            as="image"
            type="image/avif"
            fetchpriority="high"
        />
    {/if}
    <link rel="stylesheet" href="/styles/index.css?v=1.2" />
    {/if}

    <meta name="view-transition" content="same-origin" />

    <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-0T8W9GCZRL"
    ></script>
    <script>
        window.dataLayer = window.dataLayer || [];
        function gtag() {
            dataLayer.push(arguments);
        }
        gtag("js", new Date());
        gtag("consent", "default", {
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            analytics_storage: "granted",
        });
        gtag("config", "G-0T8W9GCZRL", {
            allow_ad_personalization_signals: false,
            ads_data_redaction: true,
        });
    </script>
</svelte:head>

{#if !isStandalone($page.url.pathname)}
    <StarfieldBg />
    <CloudLayer />
{/if}
<SvgDefs />
{#if !isStandalone($page.url.pathname)}
    <GlobalNav />
{/if}
<slot />
{#if !isStandalone($page.url.pathname)}
    <SiteFooter />
    <RainLayer />
{/if}
